import {ITableItem} from "shared-types/SharedTypes";
import {makeStyles} from "@material-ui/core/styles";
import {createStyles, Theme} from "@material-ui/core";
import React from "react";
import {ITableDividerHeader, ITableSelectionListItem} from "shared-components/table-seater/types";
import {GroupBase, StylesConfig} from "react-select";

const NS = 'TableSeaterHelpers';

/**
 * Material UI styles have high specificity than the style.module.scss classes, so modifications should be done here
 */
export function getMaterialStyles(useReversedColors: boolean, isWhiteTheme: boolean, classPrefix: string, isAltVersion = false) {

  return makeStyles((theme: Theme) => {

    const styles: any = {
      checkboxMenuItemFC: {
        margin: '0 5px 0 0',
        width: '100%',
        padding: '4px 0',
        background: 'transparent'
      },
      checkboxManualJoinFC: {
        margin: '0 5px 0 0',
        whiteSpace: 'nowrap'
      },
      checkbox: {
        color: theme.palette.primary.main,
        padding: 7
      },
      menuCheckbox: {
        padding: '7px 9px 3px',
        marginLeft: -7,
        fontSize: '3rem !important'
      },
      selectFC: {
        width: 175,
        // paddingTop: 8,
        flexGrow: 1,
          [`& label + .${classPrefix}-MuiInput-formControl`]: {
          marginTop: 0,
        },
          [`& label.${classPrefix}-MuiInputLabel-root`]: {
          transform: 'translateY(9px)'
        }
      },
      select: {
          [`& .${classPrefix}-MuiSelect-icon`]: {
          right: 4
        },
          [`& .${classPrefix}-MuiSelect-selectMenu`]: {
          '-webkit-mask-image': `linear-gradient(to right, rgba(0, 0, 0, 1) 80%, rgba(0, 0, 0, 0))`
        }
      },
      inputLabel: {
        transform: 'translateY(16px)',
        padding: '0 5px'
      },
      chip: {
        height: 22,
        maxWidth: 'calc(100% - 5px)',
        marginLeft: 4,
        '&:first-child': {
          marginLeft: 0
        },
        [`& .${classPrefix}-MuiChip-label`]: {
          paddingLeft: 7,
          paddingRight: 7
        }
      },
      menuItem: {
        justifyContent: 'space-between',
        padding: '0 10px',
        ['& label svg:last-child']: {
          display: 'none'
        }
      },
      menuItemSelected: {
        backgroundColor: '#ebebeb !important',
        ['& label svg:first-child']: {
          display: 'none'
        },
        ['& label svg:last-child']: {
          display: 'inline-block'
        }
      },
      isValidText: {
        color: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        backgroundColor: 'transparent'
      },
      isValidChip: {
        color: theme.palette.success.dark,
        borderColor: theme.palette.success.main,
        backgroundColor: theme.palette.success.light,
      },
      isFreeText: {
        color: theme.palette.warning.main,
        borderColor: theme.palette.warning.main,
        backgroundColor: 'transparent'
      },
      isFreeChip: {
        color: theme.palette.warning.dark,
        borderColor: theme.palette.warning.main,
        backgroundColor: theme.palette.warning.light,
      },
      isInvalidText: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        backgroundColor: 'transparent'
      },
      isInvalidChip: {
        color: theme.palette.error.dark,
        borderColor: theme.palette.error.main,
        backgroundColor: theme.palette.error.light,
      },
      isAltBg: {
        borderTop: 'solid rgba(0,0,0,0.2) 1px'
      }
    };

    if (useReversedColors) {
      styles.checkbox.color = 'rgba(255,255,255,0.8)';
      styles.checkboxManualJoinFC.color = 'white';
      styles.select.color = 'white';
      styles.select[`& .${classPrefix}-MuiSelect-icon`].color = 'white';
      styles.select[`& .${classPrefix}-MuiInput-root:before`] = {
        borderBottomColor: 'rgba(255,255,255,0.5) !important'
      };
      styles.chip.color = 'rgba(255,255,255,0.8)';
      styles.chip.borderColor = 'rgba(255,255,255,0.8)';
      styles.chip.backgroundColor = 'rgba(255,255,255,0.08)';

      if (isWhiteTheme) {
        styles.select[`& .${classPrefix}-MuiInput-underline:after`] = {
          borderBottomColor: 'rgba(255,255,255,0.7)'
        }
        styles.selectFC[`& .${classPrefix}-MuiInput-underline:after`] = {
          borderBottomColor: 'rgba(255,255,255,0.7)'
        }
        styles.selectFC[`& label.${classPrefix}-MuiInputLabel-root`].color = 'rgba(255,255,255,0.6)';
        styles.selectFC[`& label.${classPrefix}-MuiInputLabel-root.Mui-focused`] = {
          color: '#fff'
        }
      }
    }

    if (isAltVersion) {
      styles.chip.maxWidth = 'auto';
      // styles.chip.pointerEvents = 'none';
    }


    return createStyles(styles);
  });
}

export function getReactSelectStyles(useReversedColors: boolean, isTypeRecurring = false, isMobile = false):  StylesConfig<ITableDividerHeader> {
  return {
    menu: (provided: any, state: any) => ({
      ...provided,
      borderRadius: '0 0 5px 5px',
      transform: 'translateY(-50%)'
    }),
    menuList: (provided: any, state: any) => ({
      ...provided,
      maxHeight: 600
    }),
    menuPortal: (provided: any, state: any) => ({
      ...provided,
      zIndex: 100
    }),
    multiValue: (provided: any, state: any) => ({
      padding: 3
    }),
    valueContainer: (provided: any, state: any) => ({
      ...provided,
      padding: '2px 0',
      height: `${isTypeRecurring ? '30px' : ''}`,
      width: `${isTypeRecurring ? (isMobile ? '220px':'180px' ): ''}`,
      flex: `${isTypeRecurring && isMobile ? 'none' : 1}`,
      webkitFlex: `${isTypeRecurring && isMobile ? 'none' : 1}`,
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      background: 'transparent',
      border: 'none',
      boxShadow: 'none', // for focus outline
      cursor: 'pointer',
      minHeight: 32
    }),
    container: (provided: any, state: any) => ({
      ...provided,
      padding: 0
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      padding: 0,
      background: 'transparent'
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      color: `${useReversedColors ? '#fff' : '#666'} !important`
    }),
    indicatorSeparator: (provided: any, state: any) => ({
      ...provided,
      color: `${useReversedColors ? '#fff' : '#666'} !important`
    }),
    indicatorsContainer: (provided: any, state: any) => ({
      ...provided,
      padding: '0 8px',
    }),
    placeholder: (provided: any, state: any) => ({
      ...provided,
      color: `${useReversedColors ? '#fff' : '#666'} !important`
    }),
  }
}

export function getSectionName(
  sectionsLookup: any, // object with dynamic keys
  table: ITableItem
  ): string {
  let sectionId = table.sectionId;
  if (!table.sectionId && table.tablesList?.length) { // table joins need to get sectionId from first tablesList item
    sectionId = table.tablesList[0].sectionId;
  }
  const specifiedTable: ITableItem = sectionsLookup[sectionId];
  return specifiedTable?.name || '';
}

export function getOptionLabel(
  sectionsLookup: any, // object with dynamic keys
  table: ITableItem,
  isForChips?: boolean
): string {

  const {name, online, walkinOnly, shared, capacity, tablesList} = table;

  const prefix = `T${name}
       ${online ? ' <span style="transform: scale(2)">*</span>' : ''}
       ${walkinOnly ? ' ~' : ''}
       ${shared ? ' ^' : ''}
       C${capacity} `;

  let sectionId = table.sectionId;
  if (!table.sectionId && tablesList?.length) { // table joins need to get sectionId from first tablesList item
    sectionId = tablesList[0].sectionId;
  }

  const specifiedTable: ITableItem = sectionsLookup[sectionId];
  if (specifiedTable) {
    return prefix + (isForChips ? ` ${specifiedTable.name}` : '');
  }

  console.warn(NS, 'Table label error', name, sectionId, sectionsLookup);
  return 'TABLE ERROR';
}

// manually finds the clicked id in case the label was clicked and the handler hasn't registered it
function getClickedMenuItemId(event: React.ChangeEvent<{ value: unknown }>): {clickedId: string, checkbox: HTMLInputElement} {

  const target: HTMLLIElement = event.nativeEvent.target as HTMLLIElement;
  const listEl: HTMLLIElement = target.closest('li') as HTMLLIElement;
  const checkbox: HTMLInputElement = listEl.querySelector('input[type="checkbox"]') as HTMLInputElement;
  const clickedId: string = listEl.getAttribute('data-value');
  return {clickedId, checkbox};
}

/**
 * Returns ids from clicked checkbox or label around checkbox.
 * Detects if label was clicked without it being registered properly and fixes it
 */
export function checkboxLabelClicks(event: React.ChangeEvent<{ value: unknown }>, manualJoinTables: boolean): string[] {
  const {checkbox, clickedId} = getClickedMenuItemId(event);

  const target = event.target;
  const ids: string[] = manualJoinTables ? target.value as string[] : [target.value as string];

  if (manualJoinTables) {
    const clickedIndex = ids.findIndex(id => id === clickedId);
    if (checkbox.checked && clickedIndex === -1) {
      ids.push(clickedId);
    } else if (!checkbox.checked && clickedIndex !== -1) {
      ids.splice(clickedIndex, 1);
    }
  }

  return ids;
}

export function getTableListWithDividerHeaders(tableList: ITableSelectionListItem[]): ITableDividerHeader[] {
  let prevSection: string;
  const tableListWithDividerHeaders: ITableDividerHeader[] = tableList.reduce((acc, table, i) => {
    const sectionId = table.sectionId || table.tablesList?.length && table.tablesList[0].sectionId;
    if (i === 0 || prevSection && sectionId !== prevSection) {
      acc.push({...table, isDivider: true, _id: 'divider-' + sectionId}); // dividers can't have same _id or else will cause react "key" problems when rendering list
    }
    acc.push(table);
    prevSection = sectionId;
    return acc;
  }, []);

  return tableListWithDividerHeaders;
}

export function autoScrollTableSeater(preselectedTableIds: string[], tableListWithDividerHeaders: ITableDividerHeader[]): void {
  let firstId: string;
  let orderNum: number = tableListWithDividerHeaders.length;
  preselectedTableIds.forEach((id) => {
    const order = tableListWithDividerHeaders.findIndex((element) => {
      return element._id === id;
    });
    if(order < orderNum) {
      firstId = id;
      orderNum = order;
    }
  });

  if(firstId) {
    const _ref = document.querySelectorAll('li[value^="'+firstId+'"]');
    if(_ref && _ref.length) {
      const selectedFirstOption = _ref[0].closest('.auto-scroll-selector');
      selectedFirstOption && selectedFirstOption.scrollIntoView({behavior: "auto", block: "center", inline: "nearest"});
    }
  }
}

class TableSeaterHelpers {
  // stores active mutli-select table ids temporarily outside of react component for better performance on long lists
  private activeMultiSelectTableIds: string[] = null;

  setActiveMultiSelectTableIds(ids: string[]): void {
    this.activeMultiSelectTableIds = ids;
  }

  getActiveMultiSelectTableIds(): string[] {
    return this.activeMultiSelectTableIds;
  }

}

// singleton pattern
const instance = new TableSeaterHelpers();
export {instance as TableSeaterHelpers};
